import React from 'react';
import { useLocation } from 'react-router-dom';
import type { Migration } from '../../types';
import type {
  SnapshotComparisonResult,
  SnapshotResourceTypeGroup,
  ChangeStatus,
  ComparisonPropertyChange,
} from '../../types/snapshotComparison';
import { ui } from '../../config/uiSizes';
import { mockFetchSnapshotComparison, liveMockFetchSnapshotComparison } from '../../utils/mockComparisonData';
import { fetchSnapshotComparison } from '../../services/SnapshotComparison/fetchSnapshotComparisonService';
import { useAuth } from '../../hooks/auth';
import { SnapshotCompToolFAQModal } from '../../components/Modals/Snapshot/SnapshotCompToolFAQModal';
import { toFriendlyName, formatValue, camelCaseToTitleCase, formatPropertyName, extractResourceName, formatUtcTimestampLocal } from '../../utils/formatters';
import { getTypeLabelByApiName } from '../../utils/resourceTypes';
import { logger } from '../../utils/logger';
import Spinner from '../../components/migratorDashboard/Spinner';
import ErrorAlert from '../../components/errorHandling/ErrorAlert';

// USE_MOCK_DATA = true  → static local mock data (no backend required)
// USE_LIVE_MOCK  = true  → real endpoint with hardcoded known-good params
// both false             → real endpoint using the job's own ID
const USE_MOCK_DATA = false;
const USE_LIVE_MOCK = false;

const COMPARISON_ERROR_CODE = 'comparison_failed';

// ── Hierarchical property change component ─────────────────────────────────

const PropertyChangeNode: React.FC<{
  node: ComparisonPropertyChange;
  depth?: number;
  index?: number;
}> = ({ node, depth = 0, index }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isLeaf = !hasChildren;
  const isPermissionPolicy = node.propertyName === 'permission_policies';

  // Special handling for permission_policies - breadcrumb format
  if (isPermissionPolicy && isLeaf && (node.changeType === 'Added' || node.changeType === 'Deleted')) {
    const value = node.changeType === 'Added' ? node.afterValue : node.beforeValue;
    const policyData = value as Record<string, unknown> | null;

    // Extract policy data
    const domain = (policyData?.['domain'] as string) || '';
    const entityName = (policyData?.['entityName'] as string) ||
      (policyData?.['entity_name'] as string) ||
      (policyData?.['name'] as string) || '';
    const actionSet = policyData?.['action_set'] as string[] | undefined;
    const hasAllPermissions = actionSet?.includes('*');

    const displayDomain = camelCaseToTitleCase(domain);
    const displayEntity = camelCaseToTitleCase(entityName);
    const displayAction = hasAllPermissions ? 'All Permissions' : (actionSet?.join(', ') || '—');

    // Format as breadcrumb: Domain > Entity Name > Action
    const breadcrumb = `${displayDomain} > ${displayEntity} > ${displayAction}`;

    // Alternating row colors for better readability - green for Added, red for Deleted
    const isEvenRow = index !== undefined && index % 2 === 0;
    const rowBgColor = node.changeType === 'Added'
      ? (isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25')
      : (isEvenRow ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-red-100/80 dark:bg-red-900/25');

    const hoverColor = node.changeType === 'Added'
      ? 'hover:bg-green-200 dark:hover:bg-green-900/35'
      : 'hover:bg-red-100 dark:hover:bg-red-900/35';

    return (
      <div className={`px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default ${rowBgColor}`}>
        <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 select-text`} title={breadcrumb}>
          {breadcrumb}
        </span>
      </div>
    );
  }

  if (isLeaf) {
    // Simple leaf node
    const before = formatValue(node.beforeValue);
    const after = formatValue(node.afterValue);

    // Extract resource names from Terraform-style references (e.g., ${...Division.Demo.id} → Demo)
    let beforeDisplay = extractResourceName(before);
    let afterDisplay = extractResourceName(after);

    // Format snake_case values (e.g., "group_administration" → "Group Administration")
    // Only format if it's NOT a Terraform reference (i.e., extractResourceName didn't change it)
    if (typeof node.beforeValue === 'string' && node.beforeValue.includes('_') && beforeDisplay === before && !before.includes('${') && !before.includes('.id')) {
      beforeDisplay = formatPropertyName(node.beforeValue);
    }
    if (typeof node.afterValue === 'string' && node.afterValue.includes('_') && afterDisplay === after && !after.includes('${') && !after.includes('.id')) {
      afterDisplay = formatPropertyName(node.afterValue);
    }

    // Guard against objects serialized as "[object Object]" by the backend
    if (beforeDisplay === '[object Object]') {
      if (typeof node.beforeValue === 'object' && node.beforeValue !== null) {
        const pairs = Object.entries(node.beforeValue as Record<string, unknown>)
          .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
          .map(([k, v]) => `${formatPropertyName(k)}: ${String(v)}`);
        beforeDisplay = pairs.length > 0 ? pairs.join(' · ') : '—';
      } else {
        beforeDisplay = '—';
      }
    }
    if (afterDisplay === '[object Object]') {
      if (typeof node.afterValue === 'object' && node.afterValue !== null) {
        const pairs = Object.entries(node.afterValue as Record<string, unknown>)
          .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
          .map(([k, v]) => `${formatPropertyName(k)}: ${String(v)}`);
        afterDisplay = pairs.length > 0 ? pairs.join(' · ') : '—';
      } else {
        afterDisplay = '—';
      }
    }

    // Alternating row colors for better readability
    const isEvenRow = index !== undefined && index % 2 === 0;

    // Determine actual change type (handles cases where backend marks as Modified but is actually Added/Deleted)
    let actualChangeType: ChangeStatus = node.changeType;
    if (node.changeType === 'Modified' && (node.afterValue === null || node.afterValue === undefined || node.afterValue === '')) {
      actualChangeType = 'Deleted';
    } else if (node.changeType === 'Modified' && (node.beforeValue === null || node.beforeValue === undefined || node.beforeValue === '')) {
      actualChangeType = 'Added';
    }

    // For Added/Deleted, show two-column layout: Property | Value
    if (actualChangeType === 'Added' || actualChangeType === 'Deleted') {
      const displayValue = actualChangeType === 'Added' ? afterDisplay : beforeDisplay;

      const rowBgColor = actualChangeType === 'Added'
        ? (isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25')
        : (isEvenRow ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-red-100/80 dark:bg-red-900/25');

      const hoverColor = actualChangeType === 'Added'
        ? 'hover:bg-green-200 dark:hover:bg-green-900/35'
        : 'hover:bg-red-100 dark:hover:bg-red-900/35';

      const formattedPropertyName = formatPropertyName(node.propertyName);

      return (
        <div className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default items-center ${rowBgColor}`}>
          <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`} title={formattedPropertyName}>
            {formattedPropertyName}
          </span>
          <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 truncate select-text`} title={displayValue}>
            {displayValue}
          </span>
        </div>
      );
    }

    // For Modified, show three-column layout: Property | Before | After
    const rowBgColor = isEvenRow
      ? 'bg-orange-50/40 dark:bg-orange-950/15'
      : 'bg-orange-100/90 dark:bg-orange-900/30';
    const hoverColor = 'hover:bg-orange-200 dark:hover:bg-orange-900/35';
    const formattedPropertyName = formatPropertyName(node.propertyName);

    return (
      <div className={`grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 ${hoverColor} transition-colors cursor-default items-center ${rowBgColor}`}>
        <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`} title={formattedPropertyName}>
          {formattedPropertyName}
        </span>
        <span className={`${ui.text.xs} lg:text-sm text-red-600 dark:text-red-400 truncate select-text`} title={before}>
          {beforeDisplay}
        </span>
        <span className={`${ui.text.xs} lg:text-sm text-green-600 dark:text-green-400 truncate select-text`} title={after}>
          {afterDisplay}
        </span>
      </div>
    );
  }

  // Special flat rendering for permission_policies at top level (depth 0)
  if (depth === 0 && node.propertyName === 'permission_policies' && hasChildren && node.children) {
    const addedCount = node.children.filter(c => c.changeType === 'Added').length;
    const deletedCount = node.children.filter(c => c.changeType === 'Deleted').length;
    const totalChanges = node.children.length;

    const borderColor =
      node.changeType === 'Added'
        ? 'border-green-300 dark:border-green-700'
        : node.changeType === 'Deleted'
          ? 'border-red-300 dark:border-red-700'
          : 'border-orange-300 dark:border-orange-700';

    const textColor =
      node.changeType === 'Added'
        ? 'text-green-700 dark:text-green-300'
        : node.changeType === 'Deleted'
          ? 'text-red-700 dark:text-red-300'
          : 'text-orange-700 dark:text-orange-300';

    return (
      <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
        {/* Compact property header with left border */}
        <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
              {formatPropertyName(node.propertyName)}
            </span>
            <StatusBadge status={node.changeType} />
            <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
              node.changeType === 'Deleted' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
              }`}>
              {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
            </span>
          </div>
        </div>

        {/* Dynamic layout based on what has items */}
        <div className="cursor-default">
          <div className={`grid ${addedCount > 0 && deletedCount > 0 ? ui.widthAndHeight.comparisonGrid : 'grid-cols-1'} gap-4 lg:gap-6`}>
            {/* Added column - only show if there are added items */}
            {addedCount > 0 && (
              <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                  <h4 className={`${ui.text.sm} font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                    Added ({addedCount})
                  </h4>
                </div>
                {/* Table header */}
                <div className="px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                  <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Domain {'>'}  Entity Name {'>'} Action</span>
                </div>
                <div className={`${ui.widthAndHeight.comparisonColumnHeight} overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default`}>
                  {node.children
                    .filter(c => c.changeType === 'Added')
                    .map((child, idx) => (
                      <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                    ))}
                </div>
              </div>
            )}

            {/* Deleted column - only show if there are deleted items */}
            {deletedCount > 0 && (
              <div className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                  <h4 className={`${ui.text.sm} lg:text-base font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                    Deleted ({deletedCount})
                  </h4>
                </div>
                {/* Table header */}
                <div className="px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                  <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Domain {'>'} Entity Name {'>'} Action</span>
                </div>
                <div className={`${ui.widthAndHeight.comparisonColumnHeight} overflow-y-auto bg-red-50/30 dark:bg-red-950/10 cursor-default`}>
                  {node.children
                    .filter(c => c.changeType === 'Deleted')
                    .map((child, idx) => (
                      <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Special rendering for ALL properties with children at top level (depth 0)
  if (depth === 0 && hasChildren && node.children) {
    // Check if children are containers (have their own children) or leaf nodes
    const hasContainerChildren = node.children.some(child => child.children && child.children.length > 0);

    // If children are containers, render each as a recursive sub-section
    if (hasContainerChildren) {
      const borderColor =
        node.changeType === 'Added'
          ? 'border-green-300 dark:border-green-700'
          : node.changeType === 'Deleted'
            ? 'border-red-300 dark:border-red-700'
            : 'border-orange-300 dark:border-orange-700';

      const textColor =
        node.changeType === 'Added'
          ? 'text-green-700 dark:text-green-300'
          : node.changeType === 'Deleted'
            ? 'text-red-700 dark:text-red-300'
            : 'text-orange-700 dark:text-orange-300';

      return (
        <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
          {/* Compact property header with left border */}
          <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
                {formatPropertyName(node.propertyName)}
              </span>
              <StatusBadge status={node.changeType} />
              <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                node.changeType === 'Deleted' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                }`}>
                {node.children.length} {node.children.length === 1 ? 'section' : 'sections'}
              </span>
            </div>
          </div>

          {/* Render each container child as a sub-section */}
          <div className="space-y-4 lg:space-y-6 cursor-default">
            {node.children.map((child, idx) => (
              <div key={`${child.jsonPath}-${idx}`} className="cursor-default">
                <PropertyChangeNode node={child} depth={1} index={idx} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Otherwise, children are leaf nodes - render as tables
    // Helper function to determine actual change type
    const getActualChangeType = (child: ComparisonPropertyChange): ChangeStatus => {
      if (child.changeType === 'Modified' && (child.afterValue === null || child.afterValue === undefined || child.afterValue === '')) {
        return 'Deleted';
      }
      if (child.changeType === 'Modified' && (child.beforeValue === null || child.beforeValue === undefined || child.beforeValue === '')) {
        return 'Added';
      }
      return child.changeType;
    };

    const addedCount = node.children.filter(c => getActualChangeType(c) === 'Added').length;
    const deletedCount = node.children.filter(c => getActualChangeType(c) === 'Deleted').length;
    const modifiedCount = node.children.filter(c => getActualChangeType(c) === 'Modified').length;
    const totalChanges = addedCount + deletedCount + modifiedCount;

    const borderColor =
      node.changeType === 'Added'
        ? 'border-green-300 dark:border-green-700'
        : node.changeType === 'Deleted'
          ? 'border-red-300 dark:border-red-700'
          : 'border-orange-300 dark:border-orange-700';

    const textColor =
      node.changeType === 'Added'
        ? 'text-green-700 dark:text-green-300'
        : node.changeType === 'Deleted'
          ? 'text-red-700 dark:text-red-300'
          : 'text-orange-700 dark:text-orange-300';

    return (
      <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
        {/* Compact property header with left border */}
        <div className={`border-l-4 ${borderColor} pl-4 mb-4 cursor-default select-none`}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`${ui.text.sectionTitle} font-bold ${textColor} select-text`}>
              {formatPropertyName(node.propertyName)}
            </span>
            <StatusBadge status={node.changeType} />
            <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full ${node.changeType === 'Added' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
              node.changeType === 'Deleted' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
              }`}>
              {totalChanges} {totalChanges === 1 ? 'change' : 'changes'}
            </span>
          </div>
        </div>

        {/* Dynamic layout based on what has items */}
        <div className="cursor-default">
          {(() => {
            const tableCount = (modifiedCount > 0 ? 1 : 0) + (addedCount > 0 ? 1 : 0) + (deletedCount > 0 ? 1 : 0);
            const tables = [];

            // Modified table
            const modifiedTable = modifiedCount > 0 && (
              <div key="modified" className="border-2 border-orange-200 dark:border-orange-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-orange-100 dark:bg-orange-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-orange-200 dark:border-orange-800/60 cursor-default select-none">
                  <h4 className={`${ui.text.sm} font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider`}>
                    Modified ({modifiedCount})
                  </h4>
                </div>
                {/* Table header */}
                <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800/40 cursor-default select-none">
                  <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Property</span>
                  <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Before</span>
                  <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>After</span>
                </div>
                <div className="bg-orange-50/30 dark:bg-orange-950/10">
                  {node.children
                    .filter(c => getActualChangeType(c) === 'Modified')
                    .map((child, idx) => (
                      <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                    ))}
                </div>
              </div>
            );

            // Added table
            const addedTable = addedCount > 0 && (
              <div key="added" className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                  <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                    Added ({addedCount})
                  </h4>
                </div>
                {/* Table header */}
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                  <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Property</span>
                  <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-green-50/30 dark:bg-green-950/10">
                  {node.children
                    .filter(c => getActualChangeType(c) === 'Added')
                    .map((child, idx) => (
                      <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                    ))}
                </div>
              </div>
            );

            // Deleted table
            const deletedTable = deletedCount > 0 && (
              <div key="deleted" className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                  <h4 className={`${ui.text.sm} lg:text-base font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                    Deleted ({deletedCount})
                  </h4>
                </div>
                {/* Table header */}
                <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                  <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Property</span>
                  <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Value</span>
                </div>
                <div className="bg-red-50/30 dark:bg-red-950/10">
                  {node.children
                    .filter(c => getActualChangeType(c) === 'Deleted')
                    .map((child, idx) => (
                      <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                    ))}
                </div>
              </div>
            );

            // Build tables array in order: Modified, Added, Deleted
            if (modifiedTable) tables.push(modifiedTable);
            if (addedTable) tables.push(addedTable);
            if (deletedTable) tables.push(deletedTable);

            // Layout logic: 1 table = full width, 2 tables = side by side, 3 tables = 2 on top + 1 on bottom
            if (tableCount === 1) {
              return <div>{tables[0]}</div>;
            } else if (tableCount === 2) {
              return (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
                  {tables[0]}
                  {tables[1]}
                </div>
              );
            } else if (tableCount === 3) {
              return (
                <>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
                    {tables[0]}
                    {tables[1]}
                  </div>
                  <div>
                    {tables[2]}
                  </div>
                </>
              );
            }
            return null;
          })()}
        </div>
      </div>
    );
  }

  // Special rendering for depth 1 container nodes (sub-sections like "call", "email", "message")
  // These should show their own header and tables for their children
  if (depth === 1 && hasChildren && node.children) {
    // Helper function to determine actual change type
    const getActualChangeType = (child: ComparisonPropertyChange): ChangeStatus => {
      if (child.changeType === 'Modified' && (child.afterValue === null || child.afterValue === undefined || child.afterValue === '')) {
        return 'Deleted';
      }
      if (child.changeType === 'Modified' && (child.beforeValue === null || child.beforeValue === undefined || child.beforeValue === '')) {
        return 'Added';
      }
      return child.changeType;
    };

    // Flatten children to get leaf nodes (skip intermediate container nodes)
    const getAllLeafNodes = (nodes: ComparisonPropertyChange[]): ComparisonPropertyChange[] => {
      const leaves: ComparisonPropertyChange[] = [];
      for (const node of nodes) {
        if (node.children && node.children.length > 0) {
          leaves.push(...getAllLeafNodes(node.children));
        } else {
          leaves.push(node);
        }
      }
      return leaves;
    };

    const leafChildren = getAllLeafNodes(node.children);
    const addedCount = leafChildren.filter(c => getActualChangeType(c) === 'Added').length;
    const deletedCount = leafChildren.filter(c => getActualChangeType(c) === 'Deleted').length;
    const modifiedCount = leafChildren.filter(c => getActualChangeType(c) === 'Modified').length;

    return (
      <div className="pl-4 lg:pl-6 cursor-default">
        {/* Sub-section header - simple and clean */}
        <h3 className={`${ui.text.body} font-bold text-gray-700 dark:text-gray-300 mb-3 cursor-default select-text`}>
          {formatPropertyName(node.propertyName)}
        </h3>

        {/* Tables layout */}
        {(() => {
          const tableCount = (modifiedCount > 0 ? 1 : 0) + (addedCount > 0 ? 1 : 0) + (deletedCount > 0 ? 1 : 0);
          const tables = [];

          // Modified table
          const modifiedTable = modifiedCount > 0 && (
            <div key="modified" className="border-2 border-orange-200 dark:border-orange-800/60 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-orange-100 dark:bg-orange-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-orange-200 dark:border-orange-800/60 cursor-default select-none">
                <h4 className={`${ui.text.sm} font-bold text-orange-700 dark:text-orange-300 uppercase tracking-wider`}>
                  Modified ({modifiedCount})
                </h4>
              </div>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_1.5fr_1.5fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border-b border-orange-200 dark:border-orange-800/40 cursor-default select-none">
                <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Property</span>
                <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>Before</span>
                <span className={`${ui.text.xs} font-bold text-orange-800 dark:text-orange-200 uppercase tracking-wide`}>After</span>
              </div>
              <div className="bg-orange-50/30 dark:bg-orange-950/10">
                {leafChildren
                  .filter(c => getActualChangeType(c) === 'Modified')
                  .map((child, idx) => (
                    <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                  ))}
              </div>
            </div>
          );

          // Added table
          const addedTable = addedCount > 0 && (
            <div key="added" className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                <h4 className={`${ui.text.sm} font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                  Added ({addedCount})
                </h4>
              </div>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Property</span>
                <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Value</span>
              </div>
              <div className="bg-green-50/30 dark:bg-green-950/10">
                {leafChildren
                  .filter(c => getActualChangeType(c) === 'Added')
                  .map((child, idx) => (
                    <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                  ))}
              </div>
            </div>
          );

          // Deleted table
          const deletedTable = deletedCount > 0 && (
            <div key="deleted" className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
              <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                <h4 className={`${ui.text.sm} font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                  Deleted ({deletedCount})
                </h4>
              </div>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Property</span>
                <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Value</span>
              </div>
              <div className="bg-red-50/30 dark:bg-red-950/10">
                {leafChildren
                  .filter(c => getActualChangeType(c) === 'Deleted')
                  .map((child, idx) => (
                    <PropertyChangeNode key={`${child.jsonPath}-${idx}`} node={child} depth={depth + 1} index={idx} />
                  ))}
              </div>
            </div>
          );

          // Build tables array in order: Modified, Added, Deleted
          if (modifiedTable) tables.push(modifiedTable);
          if (addedTable) tables.push(addedTable);
          if (deletedTable) tables.push(deletedTable);

          // Layout logic: 1 table = full width, 2 tables = side by side, 3 tables = 2 on top + 1 on bottom
          if (tableCount === 1) {
            return <div>{tables[0]}</div>;
          } else if (tableCount === 2) {
            return (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
                {tables[0]}
                {tables[1]}
              </div>
            );
          } else if (tableCount === 3) {
            return (
              <>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 mb-4 lg:mb-6">
                  {tables[0]}
                  {tables[1]}
                </div>
                <div>
                  {tables[2]}
                </div>
              </>
            );
          }
          return null;
        })()}
      </div>
    );
  }

  // Branch node with children - always flatten (render children directly without dropdown)
  // This prevents nested property dropdowns - only resource items should be collapsible
  if (node.children && node.children.length > 0) {
    return (
      <>
        {node.children.map((child, childIdx) => (
          <PropertyChangeNode key={`${child.jsonPath}-${childIdx}`} node={child} depth={depth} index={childIdx} />
        ))}
      </>
    );
  }

  return null;
};

// ── Status badge helpers ────────────────────────────────────────────────────

const STATUS_CONFIG: Partial<Record<ChangeStatus, { label: string; classes: string }>> = {
  Added: {
    label: 'Added',
    classes: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  Modified: {
    label: 'Modified',
    classes: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  },
  Deleted: {
    label: 'Removed',
    classes: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  },
};

const StatusBadge: React.FC<{ status: ChangeStatus }> = ({ status }) => {
  const config = STATUS_CONFIG[status];
  if (!config) return null;
  return (
    <span className={`${ui.text.xs} font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${config.classes}`}>
      {config.label}
    </span>
  );
};

// ── Resource type accordion row ─────────────────────────────────────────────

const ResourceTypeRow: React.FC<{ group: SnapshotResourceTypeGroup }> = ({ group }) => {
  const { added, modified, deleted } = group.summary;
  const total = added + modified + deleted;
  const friendlyName = getTypeLabelByApiName(toFriendlyName(group.resourceType));
  const changedInstances = group.instances.filter((i) => i.status !== 'Unchanged');

  return (
    <details className="group border-b border-gray-100 dark:border-gray-800 last:border-0">
      <summary className={`flex items-center justify-between ${ui.page.rowPadding} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors list-none select-none`}>
        <div className="flex items-center gap-2 min-w-0">
          {/* Expand chevron */}
          <svg
            className={`${ui.svg.sm} shrink-0 text-gray-400 transition-transform duration-200 group-open:rotate-90`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {/* Blue total badge — before the name */}
          <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2 py-0.5 rounded-full shrink-0`}>
            {total}
          </span>
          <span className={`${ui.text.body} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={friendlyName}>
            {friendlyName}
          </span>
        </div>
        {/* Summary pills — right side */}
        <div className="flex items-center gap-1.5 shrink-0 ml-3">
          {added > 0 && (
            <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300`}>
              +{added}
            </span>
          )}
          {modified > 0 && (
            <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300`}>
              ~{modified}
            </span>
          )}
          {deleted > 0 && (
            <span className={`${ui.text.xs} font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>
              -{deleted}
            </span>
          )}
        </div>
      </summary>

      {/* Individual resource items (Unchanged instances are excluded) */}
      <div className="bg-gray-50 dark:bg-gray-800/40 border-t border-gray-100 dark:border-gray-800">
        {changedInstances.map((item) => {
          // Item is expandable if it has property changes OR afterState (for Added items) OR beforeState (for Deleted items)
          const hasDetails = item.properties.length > 0 || (item.status === 'Added' && item.afterState) || (item.status === 'Deleted' && item.beforeState);

          // Format displayName by removing underscores and adding spaces before capitals after lowercase letters
          const displayText = group.resourceType === 'genesyscloud_location'
            ? formatPropertyName(item.label)
            : (item.displayName || item.label || '')
              .replace(/_/g, ' ')  // Replace underscores with spaces
              .replace(/([a-z])([A-Z])/g, '$1 $2');  // Add space before capital letters after lowercase (e.g., camelCase -> camel Case)

          return (
            <details
              key={item.label}
              className={`group/item border-b border-gray-100 dark:border-gray-800/60 last:border-0 ${hasDetails ? 'cursor-pointer' : ''}`}
            >
              <summary className={`flex items-center justify-between pl-8 pr-4 py-2.5 transition-colors list-none ${item.status === 'Added' ? 'bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30' :
                item.status === 'Modified' ? 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/30' :
                  item.status === 'Deleted' ? 'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30' :
                    'hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${hasDetails ? 'cursor-pointer' : 'cursor-default'}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  {hasDetails ? (
                    <svg
                      className={`${ui.svg.sm} shrink-0 transition-transform duration-200 group-open/item:rotate-90 ${item.status === 'Added' ? 'text-green-400 dark:text-green-500' :
                        item.status === 'Modified' ? 'text-orange-400 dark:text-orange-500' :
                          item.status === 'Deleted' ? 'text-red-400 dark:text-red-500' :
                            'text-gray-300 dark:text-gray-600'
                        }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <span className="w-5 shrink-0" />
                  )}
                  <span
                    className={`${ui.text.label} truncate font-medium select-text ${item.status === 'Added' ? 'text-green-700 dark:text-green-300' :
                      item.status === 'Modified' ? 'text-orange-700 dark:text-orange-300' :
                        item.status === 'Deleted' ? 'text-red-700 dark:text-red-300' :
                          'text-gray-700 dark:text-gray-300'
                      }`}
                    title={displayText}
                  >
                    {displayText}
                  </span>
                </div>
                <StatusBadge status={item.status} />
              </summary>

              {/* Property-level changes OR afterState for Added items */}
              {hasDetails ? (() => {
                // For Modified/Deleted items with property changes
                if (item.properties.length > 0) {
                  return (
                    <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                      {/* Render ALL properties with the same fancy layout - parent changeType determines styling */}
                      {item.properties.map((prop, idx) => (
                        <PropertyChangeNode key={`${prop.jsonPath}-${idx}`} node={prop} depth={0} index={idx} />
                      ))}
                    </div>
                  );
                }

                // ── Auth Role Resource (Added items) ──────────────────────────────────────
                // For Added items with afterState (e.g., new Auth Roles)
                if (item.status === 'Added' && item.afterState) {
                  const state = item.afterState as Record<string, unknown>;
                  const hasPermissionPolicies = Array.isArray(state['permission_policies']) && (state['permission_policies'] as unknown[]).length > 0;

                  if (hasPermissionPolicies) {
                    const policies = state['permission_policies'] as Array<Record<string, unknown>>;

                    return (
                      <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                        <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                          {/* Permission Policies header */}
                          <div className="border-l-4 border-green-300 dark:border-green-700 pl-4 mb-4 cursor-default select-none">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`${ui.text.sectionTitle} font-bold text-green-700 dark:text-green-300 select-text`}>
                                Permission Policies
                              </span>
                              <StatusBadge status="Added" />
                              <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`}>
                                {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
                              </span>
                            </div>
                          </div>

                          {/* Table */}
                          <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                              <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                                Added ({policies.length})
                              </h4>
                            </div>
                            {/* Table header */}
                            <div className="px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                              <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>
                                Domain {'>'} Entity Name {'>'} Action
                              </span>
                            </div>
                            {/* Policy rows */}
                            <div className="max-h-96 overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default">
                              {policies.map((policy, idx) => {
                                const domain = (policy['domain'] as string) || '';
                                const entityName = (policy['entity_name'] as string) || (policy['entityName'] as string) || '';
                                const actionSet = policy['action_set'] as string[] | undefined;
                                const hasAllPermissions = actionSet?.includes('*');

                                const displayDomain = camelCaseToTitleCase(domain);
                                const displayEntity = camelCaseToTitleCase(entityName);
                                const displayAction = hasAllPermissions ? 'All Permissions' : (actionSet?.join(', ') || '—');
                                const breadcrumb = `${displayDomain} > ${displayEntity} > ${displayAction}`;

                                const isEvenRow = idx % 2 === 0;
                                const rowBgColor = isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25';

                                return (
                                  <div
                                    key={idx}
                                    className={`px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default ${rowBgColor}`}
                                  >
                                    <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 select-text`} title={breadcrumb}>
                                      {breadcrumb}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // For other Added items, show state properties as a simple table
                  const stateEntries = Object.entries(state).filter(([_, value]) => value !== null && value !== undefined);

                  if (stateEntries.length > 0) {
                    return (
                      <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                        <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                          {/* Properties header */}
                          <div className="border-l-4 border-green-300 dark:border-green-700 pl-4 mb-4 cursor-default select-none">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`${ui.text.sectionTitle} font-bold text-green-700 dark:text-green-300 select-text`}>
                                Properties
                              </span>
                              <StatusBadge status="Added" />
                              <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300`}>
                                {stateEntries.length} {stateEntries.length === 1 ? 'property' : 'properties'}
                              </span>
                            </div>
                          </div>

                          {/* Table */}
                          <div className="border-2 border-green-200 dark:border-green-800/60 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-green-100 dark:bg-green-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-green-200 dark:border-green-800/60 cursor-default select-none">
                              <h4 className={`${ui.text.sm} lg:text-base font-bold text-green-700 dark:text-green-300 uppercase tracking-wider`}>
                                Added ({stateEntries.length})
                              </h4>
                            </div>
                            {/* Table header */}
                            <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800/40 cursor-default select-none">
                              <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Property</span>
                              <span className={`${ui.text.xs} font-bold text-green-800 dark:text-green-200 uppercase tracking-wide`}>Value</span>
                            </div>
                            {/* Property rows */}
                            <div className="max-h-96 overflow-y-auto bg-green-50/30 dark:bg-green-950/10 cursor-default">
                              {stateEntries.map(([key, value], idx) => {
                                const formattedKey = formatPropertyName(key);
                                const isEvenRow = idx % 2 === 0;
                                const rowBgColor = isEvenRow ? 'bg-green-50/40 dark:bg-green-950/15' : 'bg-green-100/80 dark:bg-green-900/25';

                                // For member_ids and column_names specifically, render as nested scrollable table
                                if ((key === 'member_ids' || key === 'column_names') && Array.isArray(value) && value.length > 0) {
                                  return (
                                    <div
                                      key={idx}
                                      className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default ${rowBgColor}`}
                                    >
                                      <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium select-text`} title={formattedKey}>
                                        {formattedKey}
                                      </span>
                                      <div className="border border-green-200 dark:border-green-700 rounded overflow-hidden">
                                        <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-800">
                                          {value.map((item, itemIdx) => {
                                            const displayItem = extractResourceName(String(item));
                                            const itemEvenRow = itemIdx % 2 === 0;
                                            const itemBgColor = itemEvenRow ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800';
                                            return (
                                              <div
                                                key={itemIdx}
                                                className={`px-2 py-1 border-b border-green-100 dark:border-green-800/40 last:border-0 ${itemBgColor}`}
                                              >
                                                <span className={`${ui.text.xs} text-gray-800 dark:text-gray-100 select-text`} title={displayItem}>
                                                  {displayItem}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                // For all other values (including other arrays)
                                let displayValue: string;
                                if (Array.isArray(value)) {
                                  displayValue = value.map(v => extractResourceName(String(v))).join(', ');
                                } else if (typeof value === 'boolean') {
                                  displayValue = value ? 'true' : 'false';
                                } else if (typeof value === 'object' && value !== null) {
                                  const pairs = Object.entries(value as Record<string, unknown>)
                                    .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
                                    .map(([k, v]) => `${formatPropertyName(k)}: ${Array.isArray(v) ? (v as unknown[]).join(', ') : String(v)}`);
                                  displayValue = pairs.length > 0 ? pairs.join(' · ') : '—';
                                } else {
                                  const raw = String(value);
                                  displayValue = raw === '[object Object]' ? '—' : extractResourceName(raw);
                                }

                                return (
                                  <div
                                    key={idx}
                                    className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-green-200 dark:hover:bg-green-900/35 transition-colors cursor-default items-center ${rowBgColor}`}
                                  >
                                    <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`} title={formattedKey}>
                                      {formattedKey}
                                    </span>
                                    <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 truncate select-text`} title={displayValue}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Fallback for items with no displayable state
                  return (
                    <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60 p-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        New resource added (no property details available)
                      </div>
                    </div>
                  );
                }

                // ── Deleted Resource (show beforeState) ──────────────────────────────────────
                // For Deleted items with beforeState
                if (item.status === 'Deleted' && item.beforeState) {
                  const state = item.beforeState as Record<string, unknown>;
                  const stateEntries = Object.entries(state).filter(([_, value]) => value !== null && value !== undefined);

                  if (stateEntries.length > 0) {
                    return (
                      <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60">
                        <div className="pl-8 lg:pl-10 pr-4 py-3 lg:py-4 cursor-default">
                          {/* Properties header */}
                          <div className="border-l-4 border-red-300 dark:border-red-700 pl-4 mb-4 cursor-default select-none">
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`${ui.text.sectionTitle} font-bold text-red-700 dark:text-red-300 select-text`}>
                                Properties
                              </span>
                              <StatusBadge status="Deleted" />
                              <span className={`${ui.text.xs} font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300`}>
                                {stateEntries.length} {stateEntries.length === 1 ? 'property' : 'properties'}
                              </span>
                            </div>
                          </div>

                          {/* Table */}
                          <div className="border-2 border-red-200 dark:border-red-800/60 rounded-lg overflow-hidden shadow-sm">
                            <div className="bg-red-100 dark:bg-red-900/30 px-3 lg:px-4 py-2 lg:py-2.5 border-b-2 border-red-200 dark:border-red-800/60 cursor-default select-none">
                              <h4 className={`${ui.text.sm} lg:text-base font-bold text-red-700 dark:text-red-300 uppercase tracking-wider`}>
                                Deleted ({stateEntries.length})
                              </h4>
                            </div>
                            {/* Table header */}
                            <div className="grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 cursor-default select-none">
                              <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Property</span>
                              <span className={`${ui.text.xs} font-bold text-red-800 dark:text-red-200 uppercase tracking-wide`}>Value</span>
                            </div>
                            {/* Property rows */}
                            <div className="max-h-96 overflow-y-auto bg-red-50/30 dark:bg-red-950/10 cursor-default">
                              {stateEntries.map(([key, value], idx) => {
                                const formattedKey = formatPropertyName(key);
                                const isEvenRow = idx % 2 === 0;
                                const rowBgColor = isEvenRow ? 'bg-red-50/40 dark:bg-red-950/15' : 'bg-red-100/80 dark:bg-red-900/25';

                                // For member_ids and column_names specifically, render as nested scrollable table
                                if ((key === 'member_ids' || key === 'column_names') && Array.isArray(value) && value.length > 0) {
                                  return (
                                    <div
                                      key={idx}
                                      className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-red-100 dark:hover:bg-red-900/35 transition-colors cursor-default ${rowBgColor}`}
                                    >
                                      <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium select-text`} title={formattedKey}>
                                        {formattedKey}
                                      </span>
                                      <div className="border border-red-200 dark:border-red-700 rounded overflow-hidden">
                                        <div className="max-h-32 overflow-y-auto bg-white dark:bg-gray-800">
                                          {value.map((item, itemIdx) => {
                                            const displayItem = extractResourceName(String(item));
                                            const itemEvenRow = itemIdx % 2 === 0;
                                            const itemBgColor = itemEvenRow ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800';
                                            return (
                                              <div
                                                key={itemIdx}
                                                className={`px-2 py-1 border-b border-red-100 dark:border-red-800/40 last:border-0 ${itemBgColor}`}
                                              >
                                                <span className={`${ui.text.xs} text-gray-800 dark:text-gray-100 select-text`} title={displayItem}>
                                                  {displayItem}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                // For resources arrays, render them specially
                                if (key === 'resources' && Array.isArray(value) && value.length > 0) {
                                  return (
                                    <div
                                      key={idx}
                                      className={`px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-red-100 dark:hover:bg-red-900/35 transition-colors cursor-default ${rowBgColor}`}
                                    >
                                      <div className="font-medium text-gray-800 dark:text-gray-200 mb-2">
                                        <span className={`${ui.text.xs} lg:text-sm select-text`}>{formattedKey}</span>
                                      </div>
                                      <div className="border border-red-200 dark:border-red-700 rounded overflow-hidden">
                                        <div className="max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
                                          {value.map((resource, resIdx) => {
                                            const res = resource as Record<string, unknown>;
                                            const resEntries = Object.entries(res).filter(([_, v]) => v !== null && v !== undefined);
                                            const resEvenRow = resIdx % 2 === 0;
                                            const resBgColor = resEvenRow ? 'bg-gray-50 dark:bg-gray-900' : 'bg-white dark:bg-gray-800';
                                            return (
                                              <div key={resIdx} className={`px-2 py-2 border-b border-red-100 dark:border-red-800/40 last:border-0 ${resBgColor}`}>
                                                {resEntries.map(([k, v], kIdx) => (
                                                  <div key={kIdx} className="flex gap-2 text-xs mb-1 last:mb-0">
                                                    <span className="font-medium text-gray-600 dark:text-gray-400 select-text">{formatPropertyName(k)}:</span>
                                                    <span className="text-gray-800 dark:text-gray-100 select-text">{String(v)}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                // For all other values (including other arrays)
                                let displayValue: string;
                                if (Array.isArray(value)) {
                                  displayValue = value.map(v => extractResourceName(String(v))).join(', ');
                                } else if (typeof value === 'boolean') {
                                  displayValue = value ? 'true' : 'false';
                                } else if (typeof value === 'object' && value !== null) {
                                  const pairs = Object.entries(value as Record<string, unknown>)
                                    .filter(([, v]) => v !== null && v !== undefined && String(v) !== '[object Object]')
                                    .map(([k, v]) => `${formatPropertyName(k)}: ${Array.isArray(v) ? (v as unknown[]).join(', ') : String(v)}`);
                                  displayValue = pairs.length > 0 ? pairs.join(' · ') : '—';
                                } else {
                                  const raw = String(value);
                                  displayValue = raw === '[object Object]' ? '—' : extractResourceName(raw);
                                }

                                return (
                                  <div
                                    key={idx}
                                    className={`grid grid-cols-[1fr_2fr] gap-2 lg:gap-3 px-2 lg:px-4 py-1.5 lg:py-2 border-b border-gray-100 dark:border-gray-800/40 last:border-0 hover:bg-red-100 dark:hover:bg-red-900/35 transition-colors cursor-default items-center ${rowBgColor}`}
                                  >
                                    <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 font-medium truncate select-text`} title={formattedKey}>
                                      {formattedKey}
                                    </span>
                                    <span className={`${ui.text.xs} lg:text-sm text-gray-800 dark:text-gray-200 truncate select-text`} title={displayValue}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Fallback for deleted items with no displayable state
                  return (
                    <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800/60 p-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Resource deleted (no property details available)
                      </div>
                    </div>
                  );
                }

                return null;
              })() : null}
            </details>
          );
        })}
      </div>
    </details>
  );
};

// ── Page component ───────────────────────────────────────────────────────────

const SnapshotComparisonToolPage: React.FC = () => {
  const location = useLocation();
  const { accessToken } = useAuth();
  const job = location.state?.job as Migration | undefined;

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: SnapshotComparisonTool mounted', { jobId: job?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [comparisonData, setComparisonData] = React.useState<SnapshotComparisonResult | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isFAQOpen, setIsFAQOpen] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);

  const formattedTimestamp = job?.createdAtUtc
    ? formatUtcTimestampLocal(job.createdAtUtc)
    : '—';

  React.useEffect(() => {
    if (!job?.id) return;

    let cancelled = false;

    const loadComparison = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await (
          USE_MOCK_DATA
            ? mockFetchSnapshotComparison(job.id)
            : USE_LIVE_MOCK
              ? liveMockFetchSnapshotComparison(accessToken ?? '')
              : fetchSnapshotComparison(accessToken ?? '', job.id)
        );

        if (!cancelled) {
          setComparisonData(data);
        }
      } catch (err) {
        logger.error('Failed to load snapshot comparison', {
          error: err instanceof Error ? err.message : String(err),
          jobId: job.id
        });

        if (!cancelled) {
          setError(COMPARISON_ERROR_CODE);
          setComparisonData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadComparison();

    return () => {
      cancelled = true;
    };
  }, [job, accessToken, retryCount]);

  return (
    <>
      <div
        className="px-6 py-6 xl:py-5 lg:py-4 md:py-3 md:px-4 lg:px-5 transition-[padding] duration-300"
        style={isFAQOpen
          ? { paddingRight: 'min(496px, calc(38vw + 16px))' }
          : { maxWidth: '1536px', marginLeft: 'auto', marginRight: 'auto' }}
      >
        <div className="flex flex-col rounded-2xl border-1 border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mt-0 mb-2 p-1">
          <div className={`flex flex-col flex-1 rounded-2xl border border-gray-200 bg-gray-100 shadow-xl dark:border-gray-600 dark:bg-gray-700 overflow-hidden m-2 ${ui.page.cardPadding}`}>

            {/* ── Top info card ───────────────────────────────────── */}
            <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding} ${ui.mb.section}`}>

              {/* Title */}
              <div className={`flex items-start ${ui.mb.sm}`}>
                <div className="inline-flex flex-col items-start">
                  <p className={`${ui.text.xs} font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1`}>Migration Name</p>
                  <h2 className={`${ui.text.pageTitle} font-extrabold leading-none tracking-tight text-gray-800 dark:text-gray-100`}>
                    {job?.name ?? 'Objects Changed During Migration'}
                  </h2>
                  <div aria-hidden="true" className="mt-2 h-1 w-16 bg-blue-600 dark:bg-blue-500 rounded" />
                </div>
              </div>

              {/* Description */}
              <p className={`${ui.text.body} text-gray-600 dark:text-gray-300 ${ui.mb.section}`}>
                The Snapshot Comparison Tool provides a snapshot comparison view that helps you quickly understand what changed during your migration and determine
                whether any updates need adjustment. This screen compares your target environment before and after the migration, highlighting only the differences
                so you can focus on what matters most.{' '}
                <button
                  type="button"
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  onClick={() => setIsFAQOpen(true)}
                >
                  View Manual Rollback Guidance
                </button>
              </p>

              {/* Metadata row */}
              <div className={`grid grid-cols-2 ${ui.gap.md}`}>

                {/* Timestamp */}
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                  <div className="flex-shrink-0 rounded-md bg-purple-100 dark:bg-purple-900/40 p-2">
                    <svg className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>Migration Timestamp</p>
                    <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={formattedTimestamp}>{formattedTimestamp}</p>
                  </div>
                </div>

                {/* Target Org */}
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-3">
                  <div className="flex-shrink-0 rounded-md bg-emerald-100 dark:bg-emerald-900/40 p-2">
                    <svg className={`${ui.svg.sm} text-emerald-600 dark:text-emerald-400`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide`}>Target Organization</p>
                    <p className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100 truncate`} title={job?.targetOrgName ?? '—'}>{job?.targetOrgName ?? '—'}</p>
                  </div>
                </div>

              </div>
            </div>

            {/* ── Genesys Objects Changed card ────────────────────── */}
            <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 ${ui.page.cardPadding}`}>

              {/* Header row */}
              <div className={`flex items-center justify-between ${ui.mb.title}`}>
                <div className="inline-flex flex-col items-start">

                  <div className="flex items-center gap-2">
                    {comparisonData && (
                      <span className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 px-2.5 py-0.5 rounded-full`}>
                        {comparisonData.summary.instancesAdded + comparisonData.summary.instancesModified + comparisonData.summary.instancesDeleted}
                      </span>
                    )}
                    <h3 className={`${ui.text.sectionTitle} font-bold text-gray-800 dark:text-gray-100`}>
                      Genesys Object Change Comparison
                    </h3>
                  </div>

                </div>

                {/* Legend */}
                {comparisonData && (
                  <div className="flex items-center gap-3">
                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-green-700 dark:text-green-400`}>
                      <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Added
                    </span>
                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-orange-600 dark:text-orange-400`}>
                      <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Modified
                    </span>
                    <span className={`${ui.text.xs} flex items-center gap-1 font-medium text-red-600 dark:text-red-400`}>
                      <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Removed
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              {loading && (
                <div className="flex items-center justify-center gap-3">
                  <Spinner />
                  <span className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>Loading comparison data…</span>
                </div>
              )}

              {!loading && error && (
                <ErrorAlert
                  message={error}
                  onRetry={() => setRetryCount((prev) => prev + 1)}
                />
              )}

              {!loading && !error && comparisonData && comparisonData.resourceTypes.filter(g => g.summary.added + g.summary.modified + g.summary.deleted > 0).length === 0 && (
                <div className={`py-10 text-center ${ui.text.body} text-gray-400 dark:text-gray-500 italic`}>
                  No changes detected for this migration.
                </div>
              )}

              {!loading && !error && comparisonData && comparisonData.resourceTypes.filter(g => g.summary.added + g.summary.modified + g.summary.deleted > 0).length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                  {comparisonData.resourceTypes
                    .filter((g) => g.summary.added + g.summary.modified + g.summary.deleted > 0)
                    .map((group) => (
                      <ResourceTypeRow key={group.resourceType} group={group} />
                    ))}
                </div>
              )}

              {!loading && !error && !comparisonData && (
                <div className={`py-10 text-center ${ui.text.body} text-gray-400 dark:text-gray-500 italic`}>
                  No snapshot comparison to display.
                </div>
              )}

            </div>

          </div>
        </div>
      </div>
      <SnapshotCompToolFAQModal isOpen={isFAQOpen} onClose={() => setIsFAQOpen(false)} />
    </>
  );
};

export default SnapshotComparisonToolPage;

